import { useMemo, useState } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { miktarMetni } from '../../satis-analiz/bicim';
import { AramaKutusu, aramaSuz, DetayMesaj, DetayYukleniyor } from '../../satis-analiz/detay/ortak';
import type { Donem } from '../../satis-analiz/donem';
import type { StokKaynaklari } from '../ekranKaynaklari';
import { kategoriNormalize } from '../kategori';
import { useStokSorgu } from '../useStokVerisi';

interface Ham { stok_kodu: string; urun: string; kategori: string; miktar: number; tutar: number; maliyet: number }

type Alan = 'urun' | 'kategori' | 'miktar' | 'tutar' | 'maliyet';

const KOLONLAR: Array<{ alan: Alan; ad: string; sag: boolean }> = [
  { alan: 'urun', ad: 'Ürün', sag: false },
  { alan: 'kategori', ad: 'Kategori', sag: false },
  { alan: 'miktar', ad: 'Miktar', sag: true },
  { alan: 'tutar', ad: 'Tutar', sag: true },
  { alan: 'maliyet', ad: 'Maliyet', sag: true },
];
const SAYFA_BOYU = 100;

/** Fire veren butun urunler (990062, `En cok fire veren` kartiyla ayni anahtar; ek istek yok). */
export function KayipTumUrunler({ kaynaklar, donem, sube, kategori }: {
  kaynaklar: StokKaynaklari; donem: Donem; sube: string | null; kategori: string | null;
}) {
  const [arama, setArama] = useState('');
  const [sira, setSira] = useState<{ alan: Alan; azalan: boolean }>({ alan: 'tutar', azalan: true });
  const [adet, setAdet] = useState(SAYFA_BOYU);
  const q = useStokSorgu<Ham>(kaynaklar.kayipUrun, { donem, sube, kategori, tur: 'fire' });
  const satirlar = useMemo(
    () => (q.data?.data ?? []).map((r) => ({
      kod: String(r.stok_kodu),
      urun: String(r.urun),
      kategori: kategoriNormalize(r.kategori),
      miktar: Number(r.miktar) || 0,
      tutar: Number(r.tutar) || 0,
      maliyet: Number(r.maliyet) || 0,
    })),
    [q.data],
  );
  const suzulu = useMemo(() => {
    const yon = sira.azalan ? -1 : 1;
    return [...aramaSuz(satirlar, arama, (u) => [u.urun, u.kategori, u.kod])].sort((a, b) => {
      const x = a[sira.alan];
      const y = b[sira.alan];
      const fark = typeof x === 'string' ? x.localeCompare(String(y), 'tr') : x - Number(y);
      return fark !== 0 ? fark * yon : a.urun.localeCompare(b.urun, 'tr');
    });
  }, [satirlar, arama, sira]);

  if (q.isLoading) return <DetayYukleniyor testid="kayip-tum-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="kayip-tum-hata">Ürünler yüklenemedi.</DetayMesaj>;
  if (satirlar.length === 0) return <DetayMesaj testid="kayip-tum-bos">Dönemde fire yok.</DetayMesaj>;

  const sirala = (alan: Alan) => {
    setSira((s) => (s.alan === alan ? { alan, azalan: !s.azalan } : { alan, azalan: alan !== 'urun' && alan !== 'kategori' }));
    setAdet(SAYFA_BOYU);
  };

  return (
    <div data-testid="kayip-tum" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <AramaKutusu
          testid="kayip-tum-ara"
          placeholder="Ürün, kategori ya da kod ara"
          ariaLabel="Ürün ara"
          value={arama}
          onChange={(m) => {
            setArama(m);
            setAdet(SAYFA_BOYU);
          }}
        />
        <span data-testid="kayip-tum-sayac" className="text-[12px] text-ink-500 tnum">
          {fmtNum(suzulu.length)} / {fmtNum(satirlar.length)} ürün
        </span>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="kayip-tum-tablo" className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="text-[12px] text-ink-500 border-b border-border">
              {KOLONLAR.map((k) => {
                const secili = sira.alan === k.alan;
                return (
                  <th
                    key={k.alan}
                    aria-sort={secili ? (sira.azalan ? 'descending' : 'ascending') : 'none'}
                    className={`font-medium py-2 px-2 whitespace-nowrap ${k.sag ? 'text-right' : 'text-left'}`}
                  >
                    <button
                      type="button"
                      data-testid={`kayip-tum-sirala-${k.alan}`}
                      onClick={() => sirala(k.alan)}
                      className={`focus-ring hover:text-ink-900 ${secili ? 'text-ink-900' : ''}`}
                    >
                      {k.ad}
                      <span aria-hidden="true" className="ml-1 inline-block w-2">{secili ? (sira.azalan ? '↓' : '↑') : ''}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {suzulu.slice(0, adet).map((u) => (
              <tr key={u.kod} className="border-b border-border last:border-b-0">
                <td className="py-2 px-2 text-ink-900 max-w-0 w-full truncate">{u.urun}</td>
                <td className="py-2 px-2 text-ink-500 whitespace-nowrap">{u.kategori}</td>
                <td className="py-2 px-2 text-right tnum text-ink-700 whitespace-nowrap">{miktarMetni(u.miktar)}</td>
                <td className="py-2 px-2 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(u.tutar)}</td>
                <td className="py-2 px-2 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(u.maliyet)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {suzulu.length > adet && (
        <button
          type="button"
          data-testid="kayip-tum-daha"
          onClick={() => setAdet((a) => a + SAYFA_BOYU)}
          className="focus-ring self-center rounded-md border border-border px-3 py-1.5 text-[12px] text-ink-700 hover:text-ink-900"
        >
          {fmtNum(Math.min(SAYFA_BOYU, suzulu.length - adet))} ürün daha göster
        </button>
      )}
    </div>
  );
}
